import type { TranscriptSegment } from '@ai-meeting/shared'

export interface LiveTranscriptEvent {
  event?: string
  segment_id?: string | null
  source_segment_id?: string | null
  source_segment_ids?: Array<string | number>
  batch_id?: string | number
  batchId?: string | number
  text?: string
  translation?: string
  source_text?: string
  speaker?: string | number | null
  speaker_name?: string
  speakerName?: string
  start_time?: number | null
  end_time?: number | null
  language?: string
  source_lang?: string
  target_lang?: string
  sourceLang?: string
  targetLang?: string
  translation_latency_ms?: number
  /**
   * Sent on `translation.non_streaming`: false while the service is still
   * producing words for this turn, true on the last event of the stream.
   */
  done?: boolean
  /**
   * Sent on `translation.native_non_streaming`. True means this event opens a
   * new speaker block even though the speaker has not changed — the service has
   * decided the previous block is closed. Consecutive events for one speaker
   * carry false (or omit it) and are shown as one continuous block.
   */
  new_block_speaker?: boolean
  tokens?: Array<{
    text?: string
    language?: string | null
    speaker?: string | number | null
  }>
  source_turns?: Array<{
    source_segment_ids?: Array<string | number>
    text?: string
    speaker?: string | number | null
    speaker_name?: string
    speakerName?: string
    start_time?: number | null
    end_time?: number | null
    word_count?: number
  }>
  translation_turns?: Array<{
    speaker?: string | number | null
    speaker_name?: string
    speakerName?: string
    text?: string
    batch_id?: string | number
    batchId?: string | number
  }>
}

export interface LiveTranscriptTurn {
  id: string
  sourceSegmentIds: string[]
  speaker?: string
  speakerName?: string
  sourceText?: string
  realtimeTranslation?: string
  batchTranslation?: string
  sourceLanguage?: string
  targetLanguage?: string
  startMs?: number
  endMs?: number
  isDraft: boolean
  batchId?: string
  realtimeTranslationLatencyMs?: number
  batchTranslationLatencyMs?: number
  /**
   * True when this turn's translation arrived word by word from the service
   * (`translation.non_streaming`) rather than as one finished block
   * (`translation.batch`).
   *
   * Enhanced mode fakes a word-by-word reveal for block deliveries; a turn that
   * is already streaming must not be put through that a second time, or the
   * text would be delayed twice over. Sticky once set: a later `translation.batch`
   * for the same turn replaces the text with the canonical batch copy, and the
   * turn must not start animating at that point.
   */
  translationStreamed?: boolean
  /**
   * True once the streaming service has said it is finished with this turn
   * (`done: true`). Until then the translation is shown in the same muted style
   * a half-revealed batch uses, so a sentence that is still arriving does not
   * read as a finished one.
   */
  translationStreamDone?: boolean
  /**
   * Which display block this turn belongs to, for
   * `translation.native_non_streaming`. Turns sharing an id are one block in
   * Enhanced Mode: one speaker heading, one timestamp range, text running on
   * without a break.
   *
   * The turns stay separate on purpose rather than being concatenated into one.
   * Enhanced Mode reveals a turn word by word, and the reveal driver treats a
   * finished turn as finished — text appended to it afterwards would appear all
   * at once instead of animating. Grouping at render time keeps every event its
   * own animated unit while still reading as one block.
   *
   * Absent on every other event kind, each of which is its own block.
   */
  speakerBlockId?: string
}

function secondsToMs(value: number | null | undefined): number | undefined {
  return typeof value === 'number' ? Math.round(value * 1000) : undefined
}

function normalizeSpeaker(value: string | number | null | undefined): string | undefined {
  if (value === undefined || value === null || value === '') return undefined
  return String(value)
}

function normalizeSpeakerName(value: string | null | undefined): string | undefined {
  return normalizeText(value)
}

function normalizeId(value: string | number | null | undefined): string | undefined {
  if (value === undefined || value === null || value === '') return undefined
  return String(value)
}

/** Normalizes an optional mixed-type ID list and removes duplicates. */
function normalizeIds(values: Array<string | number> | undefined): string[] {
  if (!values) return []
  return [...new Set(values.map(normalizeId).filter((value): value is string => Boolean(value)))]
}

function normalizeText(value: string | null | undefined): string | undefined {
  const text = value?.trim()
  return text ? text : undefined
}

/** Joins two distinct segment fragments without repeating identical content. */
function appendDistinctText(first?: string, second?: string): string | undefined {
  if (!first) return second
  if (!second || second === first) return first
  return `${first} ${second}`.trim()
}

/** Checks whether a card owns any ID from a source segment alias set. */
function sourceIdsOverlap(turn: LiveTranscriptTurn, sourceIds: string[]): boolean {
  if (sourceIds.length === 0) return false
  const turnIds = new Set(turn.sourceSegmentIds)
  return sourceIds.some((id) => turnIds.has(id) || turn.id === id)
}

/**
 * Identifies the fallback cards created when older AI-service payloads omit
 * `translation.final.source_segment_id`.
 */
function isUnlinkedRealtimeTurn(turn: LiveTranscriptTurn): boolean {
  return (
    turn.sourceSegmentIds.length === 0 &&
    !turn.sourceText &&
    !turn.batchTranslation &&
    Boolean(turn.realtimeTranslation)
  )
}

/**
 * Folds historical source-less realtime cards into the latest confirmed
 * Original from the same speaker. A live draft resets the fallback candidate
 * so a translation for the next sentence cannot attach to the previous one.
 *
 * @param turns Current chronological timeline snapshot.
 * @returns Canonical turns with adjacent legacy realtime cards collapsed.
 */
function coalesceUnlinkedRealtimeTurns(turns: LiveTranscriptTurn[]): LiveTranscriptTurn[] {
  const latestSourceIndexBySpeaker = new Map<string, number>()
  const result: LiveTranscriptTurn[] = []
  let changed = false

  for (const turn of turns) {
    const speaker = turn.speaker
    if (turn.isDraft && speaker) {
      latestSourceIndexBySpeaker.delete(speaker)
    }

    const sourceIndex =
      speaker && isUnlinkedRealtimeTurn(turn)
        ? latestSourceIndexBySpeaker.get(speaker)
        : undefined
    if (sourceIndex !== undefined) {
      const sourceTurn = result[sourceIndex]
      result[sourceIndex] = {
        ...sourceTurn,
        realtimeTranslation: appendDistinctText(
          sourceTurn.realtimeTranslation,
          turn.realtimeTranslation
        ),
        targetLanguage: sourceTurn.targetLanguage ?? turn.targetLanguage,
        realtimeTranslationLatencyMs:
          sourceTurn.realtimeTranslationLatencyMs ?? turn.realtimeTranslationLatencyMs,
      }
      changed = true
      continue
    }

    result.push(turn)
    if (speaker && turn.sourceText && !turn.isDraft) {
      latestSourceIndexBySpeaker.set(speaker, result.length - 1)
    }
  }

  return changed ? result : turns
}

/**
 * Finds the nearest confirmed Original eligible for source-less realtime
 * fallback matching. Encountering a newer draft for that speaker stops the
 * search to avoid attaching the next sentence to the previous one.
 *
 * @param turns Current chronological timeline snapshot.
 * @param speaker Normalized speaker identifier from translation.final.
 * @returns Matching source index, or -1 when fallback correlation is unsafe.
 */
function findLatestFallbackSourceIndex(
  turns: LiveTranscriptTurn[],
  speaker: string | undefined
): number {
  if (!speaker) return -1

  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const turn = turns[index]
    if (turn.speaker !== speaker) continue
    if (turn.isDraft) return -1
    if (turn.sourceText) return index
  }

  return -1
}

/**
 * Inserts or enriches a turn while preserving the earliest position and
 * collapsing any records that reference the same source segment IDs.
 *
 * @param turns Current chronological timeline snapshot.
 * @param patch New source/translation fields to merge into the matching turn.
 * @param preferExistingSpeaker Keeps the transcript speaker when translation metadata disagrees.
 * @returns A new timeline array with untouched turn objects preserved.
 */
function upsertTurn(
  turns: LiveTranscriptTurn[],
  patch: LiveTranscriptTurn,
  preferExistingSpeaker = false
): LiveTranscriptTurn[] {
  const matchingIndexes: number[] = []
  turns.forEach((turn, index) => {
    if (
      turn.id === patch.id ||
      sourceIdsOverlap(turn, patch.sourceSegmentIds) ||
      sourceIdsOverlap(patch, turn.sourceSegmentIds)
    ) {
      matchingIndexes.push(index)
    }
  })

  if (matchingIndexes.length === 0) return [...turns, patch]

  const firstIndex = matchingIndexes[0]
  let merged = turns[firstIndex]
  for (const index of matchingIndexes.slice(1)) {
    const next = turns[index]
    merged = {
      ...merged,
      sourceSegmentIds: [...new Set([...merged.sourceSegmentIds, ...next.sourceSegmentIds])],
      speaker: merged.speaker ?? next.speaker,
      speakerName: merged.speakerName ?? next.speakerName,
      sourceText: appendDistinctText(merged.sourceText, next.sourceText),
      realtimeTranslation: appendDistinctText(merged.realtimeTranslation, next.realtimeTranslation),
      batchTranslation: appendDistinctText(merged.batchTranslation, next.batchTranslation),
      sourceLanguage: merged.sourceLanguage ?? next.sourceLanguage,
      targetLanguage: merged.targetLanguage ?? next.targetLanguage,
      startMs:
        merged.startMs === undefined
          ? next.startMs
          : next.startMs === undefined
            ? merged.startMs
            : Math.min(merged.startMs, next.startMs),
      endMs:
        merged.endMs === undefined
          ? next.endMs
          : next.endMs === undefined
            ? merged.endMs
            : Math.max(merged.endMs, next.endMs),
      isDraft: merged.isDraft && next.isDraft,
      batchId: merged.batchId ?? next.batchId,
      realtimeTranslationLatencyMs:
        merged.realtimeTranslationLatencyMs ?? next.realtimeTranslationLatencyMs,
      batchTranslationLatencyMs: merged.batchTranslationLatencyMs ?? next.batchTranslationLatencyMs,
      translationStreamed: merged.translationStreamed || next.translationStreamed,
      translationStreamDone: merged.translationStreamDone || next.translationStreamDone,
      speakerBlockId: merged.speakerBlockId ?? next.speakerBlockId,
    }
  }

  const speaker = preferExistingSpeaker
    ? (merged.speaker ?? patch.speaker)
    : (patch.speaker ?? merged.speaker)
  const speakerName = preferExistingSpeaker
    ? (merged.speakerName ?? patch.speakerName)
    : (patch.speakerName ?? merged.speakerName)
  const nextTurn: LiveTranscriptTurn = {
    ...merged,
    ...patch,
    id: merged.sourceSegmentIds.length > 0 ? merged.id : patch.id,
    sourceSegmentIds: [...new Set([...merged.sourceSegmentIds, ...patch.sourceSegmentIds])],
    speaker,
    speakerName,
    sourceText: patch.sourceText ?? merged.sourceText,
    realtimeTranslation: patch.realtimeTranslation ?? merged.realtimeTranslation,
    batchTranslation: patch.batchTranslation ?? merged.batchTranslation,
    sourceLanguage: patch.sourceLanguage ?? merged.sourceLanguage,
    targetLanguage: patch.targetLanguage ?? merged.targetLanguage,
    startMs: patch.startMs ?? merged.startMs,
    endMs: patch.endMs ?? merged.endMs,
    batchId: patch.batchId ?? merged.batchId,
    realtimeTranslationLatencyMs:
      patch.realtimeTranslationLatencyMs ?? merged.realtimeTranslationLatencyMs,
    batchTranslationLatencyMs: patch.batchTranslationLatencyMs ?? merged.batchTranslationLatencyMs,
    // OR, not `??`: the batch copy of a streamed turn arrives without the flag,
    // and letting it clear the flag would restart the reveal animation on text
    // the user has already watched stream in.
    translationStreamed: patch.translationStreamed || merged.translationStreamed,
    // Same reason for `||`: every event before the last one carries
    // `done: false`, and the batch copy carries nothing at all.
    translationStreamDone: patch.translationStreamDone || merged.translationStreamDone,
  }

  const matchingSet = new Set(matchingIndexes)
  const result = turns.filter((_turn, index) => !matchingSet.has(index))
  result.splice(firstIndex, 0, nextTurn)
  return result
}

/**
 * Drops the current interim card before the next partial revision or final
 * source segment is inserted.
 *
 * @param turns Current chronological timeline snapshot.
 * @param speaker Speaker slot whose draft must be removed.
 * @returns Timeline without that speaker's draft card.
 */
function removeDraftForSpeaker(
  turns: LiveTranscriptTurn[],
  speaker: string | undefined
): LiveTranscriptTurn[] {
  const draftId = `draft:${speaker ?? 'unknown'}`
  return turns.filter((turn) => turn.id !== draftId)
}

/**
 * Replaces the active draft for one speaker so partial revisions never append
 * duplicate cards.
 *
 * @param turns Current chronological timeline snapshot.
 * @param event Incoming transcript partial event.
 * @returns Timeline containing at most one draft for the event speaker.
 */
function applyTranscriptPartial(
  turns: LiveTranscriptTurn[],
  event: LiveTranscriptEvent
): LiveTranscriptTurn[] {
  const speaker = normalizeSpeaker(event.speaker)
  const speakerName = normalizeSpeakerName(event.speaker_name ?? event.speakerName)
  const withoutDraft = removeDraftForSpeaker(turns, speaker)
  const sourceText = normalizeText(event.text)
  if (!sourceText) return withoutDraft

  return [
    ...withoutDraft,
    {
      id: `draft:${speaker ?? 'unknown'}`,
      sourceSegmentIds: [],
      speaker,
      speakerName,
      sourceText,
      sourceLanguage:
        event.tokens?.[0]?.language ?? event.language ?? event.source_lang ?? event.sourceLang,
      startMs: secondsToMs(event.start_time),
      endMs: secondsToMs(event.end_time),
      isDraft: true,
    },
  ]
}

/**
 * Removes the speaker draft and promotes the confirmed source segment to a
 * stable-ID timeline turn.
 *
 * @param turns Current chronological timeline snapshot.
 * @param event Incoming transcript final event.
 * @returns Timeline enriched with the authoritative source segment.
 */
function applyTranscriptFinal(
  turns: LiveTranscriptTurn[],
  event: LiveTranscriptEvent
): LiveTranscriptTurn[] {
  const speaker = normalizeSpeaker(event.speaker)
  const speakerName = normalizeSpeakerName(event.speaker_name ?? event.speakerName)
  const withoutDraft = removeDraftForSpeaker(turns, speaker)
  const sourceText = normalizeText(event.text)
  if (!sourceText) return withoutDraft

  const segmentId = normalizeId(event.segment_id)
  const startMs = secondsToMs(event.start_time)
  const id = segmentId ?? `source:${speaker ?? 'unknown'}:${startMs ?? withoutDraft.length}`
  const pendingRealtime =
    withoutDraft.length > 0 &&
    withoutDraft[withoutDraft.length - 1]?.speaker === speaker &&
    isUnlinkedRealtimeTurn(withoutDraft[withoutDraft.length - 1])
      ? withoutDraft[withoutDraft.length - 1]
      : undefined
  const sourceTurns = pendingRealtime ? withoutDraft.slice(0, -1) : withoutDraft

  return upsertTurn(sourceTurns, {
    id,
    sourceSegmentIds: segmentId ? [segmentId] : [],
    speaker,
    speakerName,
    sourceText,
    realtimeTranslation: pendingRealtime?.realtimeTranslation,
    sourceLanguage:
      event.tokens?.[0]?.language ?? event.language ?? event.source_lang ?? event.sourceLang,
    targetLanguage: pendingRealtime?.targetLanguage,
    startMs,
    endMs: secondsToMs(event.end_time),
    isDraft: false,
    realtimeTranslationLatencyMs: pendingRealtime?.realtimeTranslationLatencyMs,
  })
}

/**
 * Enriches the referenced source turn with the fast translation, or creates a
 * temporary placeholder when translation arrives before its source segment.
 *
 * @param turns Current chronological timeline snapshot.
 * @param event Incoming realtime translation final event.
 * @returns Timeline containing the source-aligned realtime translation.
 */
function applyTranslationFinal(
  turns: LiveTranscriptTurn[],
  event: LiveTranscriptEvent
): LiveTranscriptTurn[] {
  const translation = normalizeText(event.text) ?? normalizeText(event.translation)
  if (!translation) return turns

  const speaker = normalizeSpeaker(event.speaker)
  const speakerName = normalizeSpeakerName(event.speaker_name ?? event.speakerName)
  const sourceSegmentId = normalizeId(event.source_segment_id)
  const translationSegmentId = normalizeId(event.segment_id)
  const fallbackSourceIndex = sourceSegmentId
    ? -1
    : findLatestFallbackSourceIndex(turns, speaker)
  const fallbackSource = fallbackSourceIndex >= 0 ? turns[fallbackSourceIndex] : undefined
  const id = fallbackSource
    ? fallbackSource.id
    : (sourceSegmentId ??
      `translation:${translationSegmentId ?? `${speaker ?? 'unknown'}:${turns.length}`}`)
  const sourceSegmentIds = fallbackSource
    ? fallbackSource.sourceSegmentIds
    : sourceSegmentId
      ? [sourceSegmentId]
      : []

  return upsertTurn(
    turns,
    {
      id,
      sourceSegmentIds,
      speaker,
      speakerName,
      realtimeTranslation: fallbackSource
        ? appendDistinctText(fallbackSource.realtimeTranslation, translation)
        : translation,
      targetLanguage:
        event.tokens?.[0]?.language ?? event.language ?? event.target_lang ?? event.targetLang,
      startMs: secondsToMs(event.start_time),
      endMs: secondsToMs(event.end_time),
      isDraft: false,
      realtimeTranslationLatencyMs: event.translation_latency_ms,
    },
    true
  )
}

/** How one batch-shaped event is turned into turns. */
interface BatchApplyOptions {
  /** Normalized batch identifier used for deduplication and fallback keys. */
  batchId: string | undefined
  /** True when the service delivered the translation word by word itself. */
  translationStreamed: boolean
  /** True once the streaming service has finished with this turn. */
  translationStreamDone: boolean
  /**
   * Group consecutive same-speaker turns into one display block
   * (`translation.native_non_streaming`). Off for every other event kind, whose
   * turns each stand alone.
   */
  speakerBlocks: boolean
  /** True when the event asked for a new block regardless of speaker. */
  startsNewBlock: boolean
}

/**
 * Decides which display block a `translation.native_non_streaming` turn joins.
 *
 * A block runs for as long as one person holds the floor: the turn continues the
 * previous block when it names the same speaker, and opens a new one when the
 * speaker changes or the service sets `new_block_speaker`. Only a turn that
 * already belongs to a block can be continued, so a native turn following a
 * `translation.batch` turn starts fresh rather than joining it.
 *
 * @param turns Timeline so far; the last entry is the block that can be continued.
 * @param speaker Normalized speaker of the incoming turn.
 * @param startsNewBlock True when the event set `new_block_speaker`.
 * @param newBlockId Identifier to use when a new block is opened. Derived from the
 *   event so replaying the same event resolves to the same block.
 * @returns The block id to stamp on the turn.
 */
function resolveSpeakerBlockId(
  turns: LiveTranscriptTurn[],
  speaker: string | undefined,
  startsNewBlock: boolean,
  newBlockId: string
): string {
  if (!startsNewBlock) {
    const previous = turns[turns.length - 1]
    if (previous?.speakerBlockId !== undefined && previous.speaker === speaker) {
      return previous.speakerBlockId
    }
  }
  return newBlockId
}

/**
 * Aligns structured batch source turns with translation turns by index and
 * merges all source segment aliases into their chronological cards.
 *
 * @param turns Current chronological timeline snapshot.
 * @param event Incoming structured batch event.
 * @param options Batch identity, streaming flags and speaker-block grouping.
 * @returns Timeline enriched with high-accuracy batch translations.
 */
function applyBatchSourceTurns(
  turns: LiveTranscriptTurn[],
  event: LiveTranscriptEvent,
  options: BatchApplyOptions
): LiveTranscriptTurn[] {
  const { batchId, translationStreamed, translationStreamDone, speakerBlocks, startsNewBlock } =
    options
  const sourceTurns = event.source_turns ?? []
  const translationTurns = event.translation_turns ?? []
  const topLevelIds = normalizeIds(event.source_segment_ids)

  return sourceTurns.reduce((current, sourceTurn, index) => {
    const sourceIds = normalizeIds(sourceTurn.source_segment_ids)
    const fallbackId = topLevelIds[index]
    const resolvedIds = sourceIds.length > 0 ? sourceIds : fallbackId ? [fallbackId] : []
    const translationTurn =
      translationTurns[index] ??
      translationTurns.find(
        (turn) =>
          normalizeSpeaker(turn.speaker) === normalizeSpeaker(sourceTurn.speaker) &&
          normalizeText(turn.text)
      )
    const id = resolvedIds[0] ?? `batch:${batchId ?? 'unknown'}:${index}`
    const speaker = normalizeSpeaker(
      sourceTurn.speaker ?? translationTurn?.speaker ?? event.speaker
    )

    return upsertTurn(current, {
      id,
      sourceSegmentIds: resolvedIds,
      speaker,
      speakerName: normalizeSpeakerName(
        sourceTurn.speaker_name ??
          sourceTurn.speakerName ??
          translationTurn?.speaker_name ??
          translationTurn?.speakerName ??
          event.speaker_name ??
          event.speakerName
      ),
      sourceText: normalizeText(sourceTurn.text),
      batchTranslation: normalizeText(translationTurn?.text),
      sourceLanguage: event.source_lang ?? event.sourceLang,
      targetLanguage: event.target_lang ?? event.targetLang,
      startMs: secondsToMs(sourceTurn.start_time ?? event.start_time),
      endMs: secondsToMs(sourceTurn.end_time ?? event.end_time),
      isDraft: false,
      batchId,
      batchTranslationLatencyMs: event.translation_latency_ms,
      translationStreamed,
      translationStreamDone,
      // `startsNewBlock` applies to the event, so only its first turn can open a
      // block; a later same-speaker turn of that event continues the one just
      // opened rather than starting another.
      speakerBlockId: speakerBlocks
        ? resolveSpeakerBlockId(
            current,
            speaker,
            startsNewBlock && index === 0,
            `block:${batchId ?? id}:${index}`
          )
        : undefined,
    })
  }, turns)
}

/**
 * Preserves compatibility with batch payloads that provide only aggregate
 * source IDs/text instead of the newer structured `source_turns` array.
 *
 * @param turns Current chronological timeline snapshot.
 * @param event Incoming legacy or aggregate batch event.
 * @param options Batch identity, streaming flags and speaker-block grouping.
 * @returns Timeline enriched with all usable aggregate batch content.
 */
function applyBatchWithoutSourceTurns(
  turns: LiveTranscriptTurn[],
  event: LiveTranscriptEvent,
  options: BatchApplyOptions
): LiveTranscriptTurn[] {
  const { batchId, translationStreamed, translationStreamDone, speakerBlocks, startsNewBlock } =
    options
  const translationTurns = event.translation_turns ?? []
  const sourceIds = normalizeIds(event.source_segment_ids)

  if (translationTurns.length === 0) {
    const translation = normalizeText(event.translation)
    if (!translation) return turns
    const id = sourceIds[0] ?? `batch:${batchId ?? turns.length}:0`
    const speaker = normalizeSpeaker(event.speaker)
    return upsertTurn(turns, {
      id,
      sourceSegmentIds: sourceIds,
      speaker,
      speakerName: normalizeSpeakerName(event.speaker_name ?? event.speakerName),
      sourceText: normalizeText(event.source_text),
      batchTranslation: translation,
      sourceLanguage: event.source_lang ?? event.sourceLang,
      targetLanguage: event.target_lang ?? event.targetLang,
      startMs: secondsToMs(event.start_time),
      endMs: secondsToMs(event.end_time),
      isDraft: false,
      batchId,
      batchTranslationLatencyMs: event.translation_latency_ms,
      translationStreamed,
      translationStreamDone,
      speakerBlockId: speakerBlocks
        ? resolveSpeakerBlockId(turns, speaker, startsNewBlock, `block:${batchId ?? id}:0`)
        : undefined,
    })
  }

  return translationTurns.reduce((current, translationTurn, index) => {
    const sourceId = translationTurns.length === 1 ? sourceIds[0] : sourceIds[index]
    const id = sourceId ?? `batch:${batchId ?? 'unknown'}:${index}`
    const speaker = normalizeSpeaker(translationTurn.speaker ?? event.speaker)
    return upsertTurn(current, {
      id,
      sourceSegmentIds: sourceId ? [sourceId] : [],
      speaker,
      speakerName: normalizeSpeakerName(
        translationTurn.speaker_name ??
          translationTurn.speakerName ??
          event.speaker_name ??
          event.speakerName
      ),
      sourceText: translationTurns.length === 1 ? normalizeText(event.source_text) : undefined,
      batchTranslation: normalizeText(translationTurn.text),
      sourceLanguage: event.source_lang ?? event.sourceLang,
      targetLanguage: event.target_lang ?? event.targetLang,
      startMs: secondsToMs(event.start_time),
      endMs: secondsToMs(event.end_time),
      isDraft: false,
      batchId,
      batchTranslationLatencyMs: event.translation_latency_ms,
      translationStreamed,
      translationStreamDone,
      speakerBlockId: speakerBlocks
        ? resolveSpeakerBlockId(
            current,
            speaker,
            startsNewBlock && index === 0,
            `block:${batchId ?? id}:${index}`
          )
        : undefined,
    })
  }, turns)
}

/**
 * Applies one STT/translation WebSocket event to the bilingual live timeline.
 * Source IDs are authoritative, allowing source, realtime translation and
 * batch translation to arrive in any order without creating duplicate cards.
 *
 * @param turns Current chronological timeline snapshot.
 * @param event Raw AI service event, accepting both snake_case and legacy camelCase language fields.
 * @returns Updated timeline; unrelated event kinds return the original array.
 */
export function applyLiveTranscriptEvent(
  turns: LiveTranscriptTurn[],
  event: LiveTranscriptEvent
): LiveTranscriptTurn[] {
  const canonicalTurns = coalesceUnlinkedRealtimeTurns(turns)

  return sortTurnsByStart(applyEventToTurns(canonicalTurns, event))
}

/**
 * Routes one event to the applier that understands it.
 *
 * Split out so the ordering above is applied once, in one place, rather than at
 * each of the seven `return`s below.
 *
 * @param canonicalTurns Timeline with unlinked realtime turns already coalesced.
 * @param event Raw AI service event.
 * @returns Updated timeline, in whatever order the appliers left it.
 */
function applyEventToTurns(
  canonicalTurns: LiveTranscriptTurn[],
  event: LiveTranscriptEvent
): LiveTranscriptTurn[] {
  switch (event.event) {
    case 'transcript.partial':
      return applyTranscriptPartial(canonicalTurns, event)
    case 'transcript.final':
      return applyTranscriptFinal(canonicalTurns, event)
    case 'translation.final':
      return applyTranslationFinal(canonicalTurns, event)
    // Same payload shape as `translation.batch`, so it reuses the same two
    // appliers. The difference is arrival: this one lands repeatedly as the
    // service produces words, each event carrying the text so far, so the
    // turn's translation grows in place — and the turn is flagged so enhanced
    // mode shows it as it arrives instead of re-animating it word by word.
    // Same payload shape again, plus `new_block_speaker`. The translation
    // arrives complete per event, so it is NOT flagged as streamed — enhanced
    // mode animates it word by word like a batch. What differs is grouping:
    // consecutive events for one speaker share a block id and read as one
    // continuous block instead of a card each.
    case 'translation.native_non_streaming':
    case 'translation.non_streaming':
    case 'translation.batch': {
      const streamed = event.event === 'translation.non_streaming'
      const speakerBlocks = event.event === 'translation.native_non_streaming'
      const options: BatchApplyOptions = {
        batchId: normalizeId(event.batch_id ?? event.batchId),
        translationStreamed: streamed,
        translationStreamDone: streamed && event.done === true,
        speakerBlocks,
        startsNewBlock: speakerBlocks && event.new_block_speaker === true,
      }
      return (event.source_turns?.length ?? 0) > 0
        ? applyBatchSourceTurns(canonicalTurns, event, options)
        : applyBatchWithoutSourceTurns(canonicalTurns, event, options)
    }
    default:
      return canonicalTurns
  }
}

/**
 * Re-orders turns into the order the words were actually spoken.
 *
 * Every docstring in this file calls the array chronological, and until now
 * nothing made it so: `upsertTurn` appends, and in native mode each participant
 * has their own socket. Soniox closes a sentence only when more audio from that
 * speaker arrives, so A's last sentence is finalized after A starts talking
 * again — long after B's sentence, which began later, has already landed and
 * been appended. Input and Output then disagree about the order (BRAI-439).
 *
 * A turn with no `startMs` keeps its place relative to the turn it followed: it
 * takes the key of the nearest preceding turn that has one. Treating `undefined`
 * as equal to everything instead would be a non-transitive comparator, which
 * leaves `Array.sort` free to return anything at all.
 *
 * @param turns Timeline snapshot, in whatever order the events happened to land.
 * @returns The same turns, ordered by start time; input returned as-is when it
 *   is already in order, so an unchanged array keeps its identity.
 */
function sortTurnsByStart(turns: LiveTranscriptTurn[]): LiveTranscriptTurn[] {
  // The seed only applies to turns before the first timed one. `-Infinity` rather
  // than 0 so it cannot outrank a real start time — with starts always >= 0 the two
  // happen to agree, but that is a property of the data, not of this function.
  let carried = Number.NEGATIVE_INFINITY
  const keyed = turns.map((turn, index) => {
    if (typeof turn.startMs === 'number') carried = turn.startMs
    return { turn, index, key: carried }
  })

  // The index tiebreaker makes this a total order rather than leaning on sort
  // stability, and it is what keeps same-start turns in arrival order.
  const ordered = [...keyed].sort((a, b) => a.key - b.key || a.index - b.index)
  return ordered.every((entry, index) => entry.index === index)
    ? turns
    : ordered.map((entry) => entry.turn)
}

/**
 * Combines archived and active recording snapshots using source segment IDs,
 * preserving prior fields while letting the active snapshot provide updates.
 *
 * @param archived Turns captured before a recording restart.
 * @param active Turns from the current recording session.
 * @returns One deduplicated chronological timeline.
 */
export function mergeLiveTranscriptTurns(
  archived: LiveTranscriptTurn[],
  active: LiveTranscriptTurn[]
): LiveTranscriptTurn[] {
  // Sorted here too, not only in `applyLiveTranscriptEvent`: this is the other
  // way turns reach the screen — `MeetingChat` merges the archived snapshot with
  // the live one for both rendering and saving — and a restart mid-meeting is
  // exactly when the two halves interleave.
  return sortTurnsByStart(active.reduce((current, turn) => upsertTurn(current, turn), archived))
}

/**
 * Convert Enhanced-mode live turns into translation transcript segments for
 * persistence. `translation.batch` is never routed into the realtime
 * `finalTokens` stream (see `useProxySttRecording`), so batch-only providers
 * (azure/azure_speech_recognizer, gpt-realtime-whisper, google_live_translate) have no
 * `translation.final` tokens to save — their saved translation is
 * reconstructed here from the batch turns instead. Emits one segment per turn
 * that carries a `batchTranslation`, in timeline order.
 *
 * @param turns       Merged live-turn timeline (archived + active).
 * @param startedAtMs Wall-clock anchor used to convert relative `startMs` to absolute timestamps.
 */
export function buildTranslationSegmentsFromTurns(
  turns: readonly LiveTranscriptTurn[],
  startedAtMs: number
): TranscriptSegment[] {
  return turns
    .map((turn, index): TranscriptSegment | null => {
      const text = normalizeText(turn.batchTranslation)
      if (!text) return null
      const timestamp =
        typeof turn.startMs === 'number' ? startedAtMs + turn.startMs : startedAtMs + index
      const latency = turn.batchTranslationLatencyMs
      const speaker = turn.speakerName ?? turn.speaker ?? '1'
      return {
        id: `translation-turn-${turn.id}`,
        speaker,
        ...(turn.speakerName && turn.speaker ? { speakerId: turn.speaker } : {}),
        ...(turn.speakerName ? { speakerName: turn.speakerName } : {}),
        text,
        timestamp,
        // Same offset the source segments carry, from the same room clock — a
        // batch-only provider's whole Output column is built from these.
        ...(typeof turn.startMs === 'number' ? { elapsedMs: turn.startMs } : {}),
        isFinal: true,
        ...(typeof latency === 'number' ? { translation_latency_ms: latency } : {}),
      }
    })
    .filter((segment): segment is TranscriptSegment => segment !== null)
}

/**
 * Pick which translation is kept with the saved meeting (BRAI-642).
 *
 * Both describe the same speech. The inline one is what the reader watched
 * arrive word by word; the batch one is the same passage after the service
 * has seen the whole of it, and is the copy Enhanced mode shows. The batch
 * copy wins — it is the better text, and it used to be discarded the moment
 * the socket closed, leaving the minutes screen able to show only the rougher
 * of the two.
 *
 * Driven by what actually arrived rather than by which provider ran, so a
 * meeting whose batch stream produced nothing keeps its inline translation
 * instead of losing the column altogether. Batch-only providers (azure,
 * gpt-realtime-whisper) emit no inline translation at all and were already
 * taking this path.
 *
 * @returns Whichever list has content, batch first; empty when neither does.
 */
export function chooseSavedTranslationSegments(
  batch: TranscriptSegment[],
  inline: TranscriptSegment[]
): TranscriptSegment[] {
  return batch.length > 0 ? batch : inline
}
